/**
 * TelemetryDashboard.tsx
 * Feature: 154+155 - Unified Telemetry & Pipeline Dashboard
 * Author: Peter Dobler
 * Date: 2026-05-20
 * Constitution Version: 1.12.0
 *
 * Consolidates capacity-telemetry, pipeline health, insights, and
 * configuration into a single page with four tabs:
 *   Live · Health · Insights · Configure
 *
 * Tab visibility is role-gated: Health/Configure = admin,
 * Insights = manager+, Live = all authenticated.
 *
 * Conformed to Spec 197 design blueprint
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, getAuthHeader } from '../../services/apiClient';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart2,
  CheckCircle2,
  ChevronDown,
  Info,
  Settings,
  ShieldAlert,
  Trash2,
  Wrench,
  Zap,
} from 'lucide-react';
import { clsx } from 'clsx';

import {
  useLiveMetrics,
  useTelemetryStatus,
  useCapacityHistory,
  useThrottlingForecast,
} from '../../hooks/useCapacityTelemetry';
import {
  useSubjectMatterAreaList,
  useSubjectMatterAreaDetail,
  useAssignedCapacityIds,
  useInsights,
  useInsightsHistory,
} from '../../hooks/useTelemetryPipeline';
import { useProvisioningSSE } from '../../hooks/useProvisioningSSE';
import { useAuth } from '../../auth/hooks/useAuth';
import { formatEasternDate } from '@/utils/dateFormatter';

import CapacityTelemetryCard from '../../components/capacity/CapacityTelemetryCard';
import CapacityHistoryChart from '../../components/capacity/CapacityHistoryChart';
import ThrottlingForecastChart from '../../components/capacity/ThrottlingForecastChart';
import DataFreshnessIndicator from '../../components/capacity/DataFreshnessIndicator';
import { CapacityPicker } from '../../components/TelemetryPipeline/CapacityPicker';
import SlugPreview from '../../components/TelemetryPipeline/SlugPreview';
import ProvisioningModal from '../../components/TelemetryPipeline/ProvisioningModal';
import InsightsChart from '../../components/TelemetryPipeline/InsightsChart';
import { HelpTip } from '../../components/ui/HelpTip';

import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Extended,
  QuickGlance,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

import type { CapacityLiveMetrics } from '@shared/types/capacityTelemetry';
import type {
  SmaHealthSummary,
  InsightsSmaEntry,
  SseDoneEvent,
  TeardownDryRunResponse,
} from '@shared/types/telemetryPipeline';

// ---------------------------------------------------------------------------
// Tab definitions & role gating
// ---------------------------------------------------------------------------

type TabId = 'live' | 'health' | 'insights' | 'configure';

interface TabDef {
  id: TabId;
  label: string;
  Icon: React.FC<{ className?: string }>;
  minRole?: string;
}

const ALL_TABS: TabDef[] = [
  { id: 'live',      label: 'Live',      Icon: Activity   },
  { id: 'health',    label: 'Health',    Icon: ShieldAlert, minRole: 'admin'   },
  { id: 'insights',  label: 'Insights',  Icon: BarChart2,   minRole: 'manager' },
  { id: 'configure', label: 'Configure', Icon: Settings,    minRole: 'admin'   },
];

// SuperAdmin passes every check Admin passes (Spec 285, shared/access/roles.ts).
const ROLE_RANK: Record<string, number> = { viewer: 0, operator: 1, manager: 2, admin: 3, superadmin: 4 };

export function canAccess(userRole: string, minRole?: string) {
  if (!minRole) return true;
  return (ROLE_RANK[userRole.toLowerCase()] ?? 0) >= (ROLE_RANK[minRole] ?? 99);
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const SMA_API = '/api/telemetry-pipelines/subject-matter-areas';

/** Freshness dot color — status accents are intentional. */
function freshnessRing(seconds: number | null | undefined): string {
  if (seconds == null) return 'bg-[var(--color-text-secondary)] opacity-40';
  if (seconds < 600)  return 'bg-[var(--color-status-success)]';
  if (seconds < 1800) return 'bg-[var(--color-status-warning)]';
  return 'bg-[var(--color-status-danger)]';
}

/** Map a pipeline status string to a kit Badge variant. */
function statusVariant(status: string): 'success' | 'warning' | 'secondary' | 'danger' {
  switch (status) {
    case 'active':  return 'success';
    case 'pending': return 'warning';
    case 'error':   return 'danger';
    case 'retired':
    default:        return 'secondary';
  }
}

// ---------------------------------------------------------------------------
// Tab nav — token underline tabs
// ---------------------------------------------------------------------------

function TabNav({
  tabs,
  active,
  onChange,
}: {
  tabs: TabDef[];
  active: TabId;
  onChange: (id: TabId) => void;
}) {
  return (
    <div className="mb-6 flex gap-1 border-b border-[var(--color-border)]">
      {tabs.map(({ id, label, Icon }) => (
        <button
          key={id}
          onClick={() => onChange(id)}
          className={clsx(
            '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            active === id
              ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live tab — capacity detail panel
// ---------------------------------------------------------------------------

function CapacityDetailPanel({
  capacityId,
  capacityName,
}: {
  capacityId: string;
  capacityName: string;
}) {
  const [windowMinutes, setWindowMinutes] = useState(30);
  const { data: historyData, isLoading: histLoading } = useCapacityHistory(capacityId, windowMinutes);
  const { data: forecastData, isLoading: fcastLoading, error: fcastError } =
    useThrottlingForecast(capacityId);

  return (
    <Card className="bg-[var(--color-bg-subtle)] p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">{capacityName}</h3>
        <span className="text-xs text-[var(--color-text-secondary)]">History &amp; Forecast</span>
      </div>
      <div className="space-y-5">
        <CapacityHistoryChart
          history={historyData}
          loading={histLoading}
          windowMinutes={windowMinutes}
          onWindowChange={setWindowMinutes}
        />
        <ThrottlingForecastChart
          forecast={forecastData}
          loading={fcastLoading}
          error={fcastError as Error | null}
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Live tab — SMA group (collapsible capacity card grid)
// ---------------------------------------------------------------------------

interface SmaGroupProps {
  displayName: string;
  capacities: CapacityLiveMetrics[];
  degradedMode: boolean;
  collapsed: boolean;
  onToggle: () => void;
  selectedId: string;
  onSelect: (id: string) => void;
}

function SmaGroup({
  displayName,
  capacities,
  degradedMode,
  collapsed,
  onToggle,
  selectedId,
  onSelect,
}: SmaGroupProps) {
  const throttledCount = capacities.filter(
    (c) => c.currentState === 'throttled' || c.currentState === 'overloaded',
  ).length;
  const pausedCount = capacities.filter((c) => c.currentState === 'paused').length;
  const selectedInGroup = !collapsed && capacities.some((c) => c.capacityId === selectedId);
  const selectedCapacity = capacities.find((c) => c.capacityId === selectedId);

  return (
    <Card padding="none">
      {/* Group header */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-t-lg px-4 py-3 text-left transition-colors hover:bg-[var(--color-bg-subtle)]"
      >
        <ChevronDown
          className={clsx(
            'h-4 w-4 flex-shrink-0 text-[var(--color-text-secondary)] transition-transform duration-200',
            collapsed && '-rotate-90',
          )}
        />
        <span className="font-semibold text-[var(--color-text-primary)]">{displayName}</span>
        <Badge variant="secondary">{capacities.length}</Badge>
        <div className="ml-auto flex items-center gap-2">
          {throttledCount > 0 && <Badge variant="warning">{throttledCount} throttled</Badge>}
          {pausedCount > 0 && <Badge variant="secondary">{pausedCount} paused</Badge>}
        </div>
      </button>

      {/* Capacity cards grid */}
      {!collapsed && (
        <div className="border-t border-[var(--color-border)] p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {capacities.map((m) => (
              <div
                key={m.capacityId}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(selectedId === m.capacityId ? '' : m.capacityId)}
                onKeyDown={(e) => e.key === 'Enter' && onSelect(selectedId === m.capacityId ? '' : m.capacityId)}
                className={clsx(
                  'cursor-pointer rounded-lg border-2 transition-all duration-150',
                  selectedId === m.capacityId
                    ? 'border-[var(--color-accent)] shadow-md'
                    : 'border-transparent hover:border-[var(--color-border)]',
                )}
              >
                <CapacityTelemetryCard metrics={m} degradedMode={degradedMode} />
              </div>
            ))}
          </div>

          {/* Detail panel — inline below selected card's group */}
          {selectedInGroup && selectedCapacity && (
            <div className="mt-4">
              <CapacityDetailPanel
                capacityId={selectedId}
                capacityName={selectedCapacity.capacityName}
              />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Live tab
// ---------------------------------------------------------------------------

function LiveTab() {
  const [collapsedSmas, setCollapsedSmas] = useState<Set<string>>(new Set(['__unassigned__']));
  const [selectedId, setSelectedId] = useState('');

  const { data: liveData, isLoading, error } = useLiveMetrics({}, 30_000);
  const { data: statusData } = useTelemetryStatus(30_000);

  const degradedMode = liveData?.degradedMode ?? statusData?.degradedMode ?? false;
  const capacities = liveData?.capacities ?? [];

  const smaGroups = useMemo(() => {
    const bySlug = new Map<string, CapacityLiveMetrics[]>();
    for (const c of capacities) {
      const key = c.smaSlug ?? '__unassigned__';
      if (!bySlug.has(key)) bySlug.set(key, []);
      bySlug.get(key)!.push(c);
    }
    const named = [...bySlug.entries()]
      .filter(([k]) => k !== '__unassigned__')
      .sort(([, a], [, b]) =>
        (a[0].smaDisplayName ?? '').localeCompare(b[0].smaDisplayName ?? ''),
      )
      .map(([slug, caps]) => ({ slug, displayName: caps[0].smaDisplayName ?? slug, caps }));
    return { named, unassigned: bySlug.get('__unassigned__') ?? [] };
  }, [capacities]);

  const toggleSma = useCallback((key: string) => {
    setCollapsedSmas((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const handleSelect = useCallback((id: string) => {
    setSelectedId((prev) => (prev === id ? '' : id));
  }, []);

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      {liveData && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-[var(--color-text-secondary)]">
            {liveData.totalCapacities} capacities
          </span>
          {liveData.throttledCount > 0 && <Badge variant="warning">{liveData.throttledCount} throttled</Badge>}
          {liveData.pausedCount > 0 && <Badge variant="secondary">{liveData.pausedCount} paused</Badge>}
        </div>
      )}

      {/* Metric legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[var(--color-text-secondary)]">
        {[
          ['CU%', 'Compute Units consumed as a percentage of capacity allocation. Sustained high values indicate throttling risk.'],
          ['Int. Delay', 'Interactive requests slowed due to saturation. Visible to users as slow reports.'],
          ['Int. Reject', 'User-initiated queries rejected outright. Users see error messages.'],
          ['Bg. Reject',  'Background refreshes rejected. Jobs must retry.'],
        ].map(([label, tip]) => (
          <span key={label} className="flex items-center gap-1">
            <strong className="text-[var(--color-text-secondary)]">{label}</strong>
            <HelpTip text={tip} />
          </span>
        ))}
      </div>

      {/* Loading */}
      {isLoading && !liveData && <LoadingState rows={4} />}

      {/* Error */}
      {error && (
        <ErrorState
          title="Failed to load live metrics"
          message={(error as Error).message}
        />
      )}

      {/* Empty state */}
      {!isLoading && !error && capacities.length === 0 && (
        <Card>
          <EmptyState
            icon={<Activity className="h-9 w-9" />}
            title="No telemetry data yet"
            message="Set up a pipeline in the Configure tab."
          />
        </Card>
      )}

      {/* SMA groups */}
      {capacities.length > 0 && (
        <div className="space-y-3">
          {smaGroups.named.map(({ slug, displayName, caps }) => (
            <SmaGroup
              key={slug}
              displayName={displayName}
              capacities={caps}
              degradedMode={degradedMode}
              collapsed={collapsedSmas.has(slug)}
              onToggle={() => toggleSma(slug)}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          ))}
          {smaGroups.unassigned.length > 0 && (
            <SmaGroup
              displayName="Unassigned"
              capacities={smaGroups.unassigned}
              degradedMode={degradedMode}
              collapsed={collapsedSmas.has('__unassigned__')}
              onToggle={() => toggleSma('__unassigned__')}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Health tab — per-SMA expandable row
// ---------------------------------------------------------------------------

interface SmaHealthRowProps {
  sma: SmaHealthSummary;
  onRefreshList: () => void;
}

interface HealthCapacityRow {
  capacityId: string;
  capacityName: string;
  capacityType: string;
  pipelineStatus: string;
  dataFreshnessSeconds: number | null;
  lastEventAt: string | null;
  manualSetupRequired: boolean;
}

function SmaHealthRow({ sma, onRefreshList }: SmaHealthRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [dryRunData, setDryRunData] = useState<TeardownDryRunResponse | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [teardownLog, setTeardownLog] = useState<string[]>([]);
  const [teardownDone, setTeardownDone] = useState<SseDoneEvent | null>(null);
  const [teardownStreaming, setTeardownStreaming] = useState(false);
  const [teardownOpen, setTeardownOpen] = useState(false);

  const { data: detail, isLoading: detailLoading, refetch: refreshDetail } =
    useSubjectMatterAreaDetail(expanded ? sma.slug : '');

  const handleRemove = async (capacityId: string) => {
    setRemovingId(capacityId);
    setRowError(null);
    try {
      await apiClient.delete(`${SMA_API}/${sma.slug}/capacities/${capacityId}`);
      void refreshDetail();
      onRefreshList();
    } catch (err) {
      setRowError(String(err));
    } finally {
      setRemovingId(null);
    }
  };

  const handleDecommissionClick = async () => {
    setRowError(null);
    setDryRunLoading(true);
    try {
      const res = await apiClient.delete<TeardownDryRunResponse>(`${SMA_API}/${sma.slug}?dryRun=true`);
      setDryRunData(res.data);
      setConfirmOpen(true);
    } catch (err) {
      setRowError(String(err));
    } finally {
      setDryRunLoading(false);
    }
  };

  const handleConfirmDecommission = async () => {
    setConfirmOpen(false);
    setTeardownLog([]);
    setTeardownDone(null);
    setTeardownStreaming(true);
    setTeardownOpen(true);
    try {
      const authHeader = await getAuthHeader();
      const teardownHeaders: Record<string, string> = {};
      if (authHeader) teardownHeaders['Authorization'] = authHeader;
      const res = await fetch(`${SMA_API}/${sma.slug}`, { method: 'DELETE', headers: teardownHeaders });
      if (!res.body) { setTeardownStreaming(false); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop() ?? '';
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(part.slice(6)) as Record<string, unknown>;
            if (parsed['type'] === 'progress' && typeof parsed['message'] === 'string') {
              setTeardownLog((l) => [...l, parsed['message'] as string]);
            } else if (parsed['type'] === 'done') {
              setTeardownDone(parsed as unknown as SseDoneEvent);
              setTeardownStreaming(false);
              onRefreshList();
            }
          } catch {
            setTeardownLog((l) => [...l, part.slice(6)]);
          }
        }
      }
    } catch (err) {
      setRowError(String(err));
      setTeardownStreaming(false);
    }
  };

  const { pending, active, retired } = sma.pipelineStatusSummary;
  const needsAttention = pending > 0 || retired > 0;

  const capacityColumns: DataTableColumn<HealthCapacityRow>[] = [
    {
      key: 'capacity',
      header: 'Capacity',
      width: '34%',
      render: (cap) => (
        <div className={clsx(cap.pipelineStatus === 'retired' && 'opacity-50')}>
          <div className="font-medium text-[var(--color-text-primary)]">{cap.capacityName}</div>
          <div className="text-xs text-[var(--color-text-secondary)]">{cap.capacityType}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '22%',
      render: (cap) => (
        <div className={clsx('flex items-center gap-1.5', cap.pipelineStatus === 'retired' && 'opacity-50')}>
          <span className={clsx('h-2 w-2 flex-shrink-0 rounded-full', freshnessRing(cap.dataFreshnessSeconds))} />
          <Badge variant={statusVariant(cap.pipelineStatus)}>{cap.pipelineStatus}</Badge>
        </div>
      ),
    },
    {
      key: 'lastEvent',
      header: 'Last Event',
      width: '20%',
      render: (cap) => (
        <span className={clsx('text-xs text-[var(--color-text-secondary)]', cap.pipelineStatus === 'retired' && 'opacity-50')}>
          {cap.lastEventAt
            ? <DataFreshnessIndicator lastEventAt={cap.lastEventAt} degradedMode={false} compact />
            : <span className="text-[var(--color-text-secondary)] opacity-50">—</span>}
        </span>
      ),
    },
    {
      key: 'setup',
      header: 'Setup',
      width: '14%',
      render: (cap) =>
        cap.manualSetupRequired ? (
          <Badge variant="warning" leftIcon={<Wrench className="h-3 w-3" />}>
            Manual setup
          </Badge>
        ) : null,
    },
    {
      key: 'actions',
      header: '',
      width: '10%',
      align: 'right',
      render: (cap) =>
        cap.pipelineStatus === 'retired' ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={removingId === cap.capacityId}
            leftIcon={<Trash2 className="h-3 w-3" />}
            onClick={(e) => {
              e.stopPropagation();
              void handleRemove(cap.capacityId);
            }}
          >
            Remove
          </Button>
        ) : null,
    },
  ];

  const capacityRows: HealthCapacityRow[] = (detail?.capacities ?? []).map((cap) => ({
    capacityId: cap.capacityId,
    capacityName: cap.capacityName,
    capacityType: cap.capacityType,
    pipelineStatus: cap.pipelineStatus,
    dataFreshnessSeconds: cap.dataFreshnessSeconds,
    lastEventAt: cap.lastEventAt,
    manualSetupRequired: cap.manualSetupRequired ?? false,
  }));

  return (
    <>
      <Card
        padding="none"
        className={clsx(
          'transition-colors',
          needsAttention && 'border-[var(--color-status-warning)]/50',
        )}
      >
        {/* Row header */}
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex flex-1 items-center gap-3 text-left"
          >
            <ChevronDown
              className={clsx(
                'h-4 w-4 flex-shrink-0 text-[var(--color-text-secondary)] transition-transform duration-200',
                !expanded && '-rotate-90',
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[var(--color-text-primary)]">{sma.displayName}</span>
                {needsAttention && <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-status-warning)]" />}
              </div>
              <span className="block truncate font-mono text-xs text-[var(--color-text-secondary)]">
                {sma.eventHubName} · {sma.eventHubsNamespaceName}
              </span>
              <span className="block truncate text-xs text-[var(--color-text-secondary)]">
                Created {formatEasternDate(sma.createdAt)}
              </span>
            </div>
          </button>

          {/* Status pills */}
          <div className="flex flex-shrink-0 items-center gap-2">
            {active > 0 && <Badge variant="success">{active} active</Badge>}
            {pending > 0 && <Badge variant="warning">{pending} pending</Badge>}
            {retired > 0 && <Badge variant="secondary">{retired} retired</Badge>}
            <Button
              size="sm"
              variant="danger"
              isLoading={dryRunLoading}
              onClick={handleDecommissionClick}
            >
              {dryRunLoading ? 'Checking…' : 'Decommission'}
            </Button>
          </div>
        </div>

        {/* Expanded capacity table */}
        {expanded && (
          <div className="border-t border-[var(--color-border)]">
            {detailLoading ? (
              <div className="p-4">
                <LoadingState rows={3} />
              </div>
            ) : capacityRows.length > 0 ? (
              <DataTable
                columns={capacityColumns}
                rows={capacityRows}
                rowKey={(cap) => cap.capacityId}
                minWidth="44rem"
                emptyMessage="No capacities in this SMA."
              />
            ) : (
              <p className="px-4 py-4 text-sm text-[var(--color-text-secondary)]">No capacities in this SMA.</p>
            )}
          </div>
        )}

        {/* Inline error */}
        {rowError && (
          <div className="border-t border-[var(--color-status-danger)]/40 bg-[var(--color-status-danger)]/5 px-4 py-2 text-xs text-[var(--color-status-danger)]">
            {rowError}
          </div>
        )}
      </Card>

      {/* Decommission confirmation modal */}
      <Modal
        isOpen={confirmOpen && !!dryRunData}
        onClose={() => setConfirmOpen(false)}
        title={`Decommission "${sma.displayName}"?`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDecommission}>
              Confirm Decommission
            </Button>
          </>
        }
      >
        {dryRunData && (
          <>
            <p className="mb-3 text-sm text-[var(--color-text-secondary)]">This will permanently:</p>
            <ul className="mb-4 space-y-1.5 text-sm text-[var(--color-text-primary)]">
              <li>
                • Delete Event Hub{' '}
                <code className="rounded bg-[var(--color-bg-subtle)] px-1 font-mono text-xs">
                  {dryRunData.wouldDelete.eventHub}
                </code>
              </li>
              <li>• Retire {dryRunData.wouldDelete.pipelineRowsRetired} pipeline row(s)</li>
              <li>
                • Clear SMA from {dryRunData.wouldDelete.capacitiesCleared}{' '}
                capacit{dryRunData.wouldDelete.capacitiesCleared === 1 ? 'y' : 'ies'}
              </li>
            </ul>
            <div className="mb-1 rounded-lg border border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5 px-3 py-2.5 text-xs text-[var(--color-status-warning)]">
              Historical telemetry snapshots are preserved. This cannot be undone.
            </div>
          </>
        )}
      </Modal>

      {/* Teardown SSE progress modal */}
      {teardownOpen && (
        <ProvisioningModal
          title={`Decommissioning ${sma.displayName}…`}
          logLines={teardownLog}
          doneEvent={teardownDone}
          isStreaming={teardownStreaming}
          onClose={() => !teardownStreaming && setTeardownOpen(false)}
        />
      )}
    </>
  );
}

function HealthTab() {
  const { data, isLoading, refetch } = useSubjectMatterAreaList();
  const smas = data?.smas ?? [];

  // Sort: pending/retired issues first
  const sorted = useMemo(
    () =>
      [...smas].sort((a, b) => {
        const score = (s: SmaHealthSummary) =>
          (s.pipelineStatusSummary.pending > 0 ? 2 : 0) +
          (s.pipelineStatusSummary.retired > 0 ? 1 : 0);
        return score(b) - score(a);
      }),
    [smas],
  );

  const totalPending = smas.reduce((n, s) => n + s.pipelineStatusSummary.pending, 0);
  const totalRetired = smas.reduce((n, s) => n + s.pipelineStatusSummary.retired, 0);

  const attentionItems = [
    totalPending > 0 && `${totalPending} pending`,
    totalRetired > 0 && `${totalRetired} retired`,
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-4">
      {/* Attention bar */}
      {attentionItems.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-[var(--color-status-warning)]/40 bg-[var(--color-status-warning)]/5 px-4 py-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-[var(--color-status-warning)]" />
          <span className="text-sm text-[var(--color-text-primary)]">
            {attentionItems.join(' · ')}
          </span>
        </div>
      )}

      {/* Freshness legend */}
      <Card className="flex flex-wrap items-center gap-4 bg-[var(--color-bg-subtle)] px-4 py-2.5 text-xs text-[var(--color-text-secondary)]">
        {[
          { cls: 'bg-[var(--color-status-success)]', label: '< 10 min' },
          { cls: 'bg-[var(--color-status-warning)]', label: '10–30 min' },
          { cls: 'bg-[var(--color-status-danger)]',  label: '> 30 min (stalled)' },
          { cls: 'bg-[var(--color-text-secondary)] opacity-40', label: 'No events yet' },
        ].map(({ cls, label }) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className={clsx('h-2 w-2 flex-shrink-0 rounded-full', cls)} />
            {label}
          </span>
        ))}
        <HelpTip text="Freshness shows how recently events were received per capacity. Red means the Eventstream pipeline has likely stalled." />
      </Card>

      {/* Loading */}
      {isLoading && <LoadingState rows={4} />}

      {/* Empty state */}
      {!isLoading && smas.length === 0 && (
        <Card>
          <EmptyState
            icon={<Zap className="h-9 w-9" />}
            title="No SMAs provisioned"
            message="Use the Configure tab to get started."
          />
        </Card>
      )}

      {/* SMA rows */}
      <div className="space-y-2">
        {sorted.map((sma) => (
          <SmaHealthRow key={sma.slug} sma={sma} onRefreshList={refetch} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Insights tab
// ---------------------------------------------------------------------------

function toDateStr(d: Date) {
  return d.toISOString().slice(0, 10);
}

function cuBand(pct: number | null): string {
  if (pct === null) return 'bg-[var(--color-bg-subtle)]';
  if (pct >= 80) return 'bg-[var(--color-status-danger)]';
  if (pct >= 60) return 'bg-[var(--color-status-warning)]';
  return 'bg-[var(--color-accent)]';
}

interface InsightsCapacityRow {
  capacityId: string;
  capacityName: string;
  hasEventsLast24h: boolean;
  avgCuPct: number | null;
  peakCuPct: number | null;
  throttleMinutes: number;
  slaCompliancePct: number | null;
}

function SmaInsightsSection({ sma, from, to }: { sma: InsightsSmaEntry; from: string; to: string }) {
  const { data, isLoading } = useInsightsHistory(sma.slug, from, to);

  const capacityColumns: DataTableColumn<InsightsCapacityRow>[] = [
    {
      key: 'capacity',
      header: 'Capacity',
      width: '40%',
      render: (cap) => (
        <span className="text-[var(--color-text-primary)]">
          {cap.capacityName}
          {!cap.hasEventsLast24h && (
            <Badge variant="warning" className="ml-2">silent 24h</Badge>
          )}
        </span>
      ),
    },
    {
      key: 'avgCu',
      header: 'Avg CU%',
      width: '14%',
      align: 'right',
      render: (cap) => (
        <span className="text-[var(--color-text-primary)]">
          {cap.avgCuPct !== null ? `${cap.avgCuPct.toFixed(1)}%` : '—'}
        </span>
      ),
    },
    {
      key: 'peakCu',
      header: 'Peak CU%',
      width: '14%',
      align: 'right',
      render: (cap) => (
        <span
          className={clsx(
            'font-medium',
            cap.peakCuPct !== null && cap.peakCuPct >= 80
              ? 'text-[var(--color-status-danger)]'
              : 'text-[var(--color-text-primary)]',
          )}
        >
          {cap.peakCuPct !== null ? `${cap.peakCuPct.toFixed(1)}%` : '—'}
        </span>
      ),
    },
    {
      key: 'throttle',
      header: 'Throttle',
      width: '16%',
      align: 'right',
      render: (cap) => (
        <span
          className={clsx(
            cap.throttleMinutes > 0
              ? 'font-medium text-[var(--color-status-warning)]'
              : 'text-[var(--color-text-secondary)]',
          )}
        >
          {cap.throttleMinutes > 0 ? `${cap.throttleMinutes}m` : '—'}
        </span>
      ),
    },
    {
      key: 'sla',
      header: 'SLA',
      width: '16%',
      align: 'right',
      render: (cap) => (
        <span
          className={clsx(
            cap.slaCompliancePct !== null && cap.slaCompliancePct < 95
              ? 'font-medium text-[var(--color-status-warning)]'
              : 'text-[var(--color-text-primary)]',
          )}
        >
          {cap.slaCompliancePct !== null ? `${cap.slaCompliancePct.toFixed(0)}%` : '—'}
        </span>
      ),
    },
  ];

  const capacityRows: InsightsCapacityRow[] = sma.capacities.map((cap) => ({
    capacityId: cap.capacityId,
    capacityName: cap.capacityName,
    hasEventsLast24h: cap.hasEventsLast24h,
    avgCuPct: cap.avgCuPct,
    peakCuPct: cap.peakCuPct,
    throttleMinutes: cap.throttleMinutes,
    slaCompliancePct: cap.slaCompliancePct,
  }));

  return (
    <Card className="p-5">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{sma.displayName}</h3>
        <div className="flex flex-wrap gap-4 text-sm text-[var(--color-text-secondary)]">
          {sma.avgCuPct !== null && (
            <span>Avg <strong className="text-[var(--color-text-primary)]">{sma.avgCuPct.toFixed(1)}%</strong> CU</span>
          )}
          {sma.peakCuPct !== null && (
            <span>Peak <strong className={clsx(sma.peakCuPct >= 80 ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-text-primary)]')}>{sma.peakCuPct.toFixed(1)}%</strong> CU</span>
          )}
          <span>{sma.totalEvents.toLocaleString()} snapshots</span>
        </div>
      </div>

      {/* CU% over time */}
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">
        CU Utilization Over Period
      </p>
      {isLoading ? (
        <LoadingState rows={2} />
      ) : (
        <InsightsChart smaSlug={sma.slug} series={data?.series ?? []} />
      )}

      {/* Time-of-day pattern */}
      <div className="mt-5">
        <p className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-[var(--color-text-secondary)]">
          Avg CU% by Hour of Day
          <HelpTip text="Average CU utilization for each hour across the selected period. Reveals predictable load patterns. Amber dashed line = 80% threshold." />
        </p>
        <div className="flex items-end gap-0.5" style={{ height: 48 }}>
          {sma.hourlyPattern.map(({ hour, avgCuPct }) => (
            <div key={hour} className="group relative flex flex-1 flex-col items-center justify-end" style={{ height: 48 }}>
              <div
                className={clsx('w-full rounded-t transition-opacity group-hover:opacity-80', cuBand(avgCuPct))}
                style={{ height: avgCuPct !== null ? `${Math.max(avgCuPct, 2)}%` : '4%', opacity: avgCuPct === null ? 0.2 : 1 }}
              />
              {/* Tooltip on hover */}
              <div className="pointer-events-none absolute bottom-full mb-1 hidden whitespace-nowrap rounded bg-[var(--color-text-primary)] px-1.5 py-0.5 text-xs text-[var(--color-bg-card)] group-hover:block">
                {String(hour).padStart(2, '0')}:00 — {avgCuPct !== null ? `${avgCuPct.toFixed(1)}%` : 'no data'}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-1 flex justify-between text-xs text-[var(--color-text-secondary)] opacity-70">
          <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
        </div>
      </div>

      {/* Per-capacity table */}
      <div className="mt-5">
        <DataTable
          columns={capacityColumns}
          rows={capacityRows}
          rowKey={(cap) => cap.capacityId}
          minWidth="44rem"
          emptyMessage="No capacities."
        />
      </div>
    </Card>
  );
}

function InsightsTab() {
  const [from, setFrom] = useState(() => toDateStr(new Date(Date.now() - 7 * 86_400_000)));
  const [to, setTo]     = useState(() => toDateStr(new Date()));
  const { data, isLoading, error } = useInsights(from, to);

  return (
    <div className="space-y-6">
      {/* Date range */}
      <Card className="flex flex-wrap items-end gap-4 p-4">
        <Input
          type="date"
          label="From"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <Input
          type="date"
          label="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </Card>

      {isLoading && <LoadingState rows={4} />}
      {error && <ErrorState title="Failed to load insights data" />}

      {data && (
        <>
          {data.smas.map((sma) => (
            <SmaInsightsSection key={sma.slug} sma={sma} from={from} to={to} />
          ))}

          {data.smas.length === 0 && (
            <div className="flex items-center gap-3 rounded-lg border border-[var(--color-status-info)]/40 bg-[var(--color-status-info)]/5 p-4">
              <Info className="h-5 w-5 flex-shrink-0 text-[var(--color-status-info)]" />
              <p className="text-sm text-[var(--color-text-primary)]">No data for the selected period.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Configure tab (admin)
// ---------------------------------------------------------------------------

type ConfigMode = 'create' | 'add';

function ConfigureTab() {
  const [mode, setMode] = useState<ConfigMode>('create');

  // Create SMA
  const [displayName, setDisplayName] = useState('');
  const [createIds, setCreateIds]   = useState<string[]>([]);
  const [forceReassign] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Add to SMA
  const [smaSlug, setSmaSlug] = useState('');
  const [addIds, setAddIds]   = useState<string[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const sseCreate = useProvisioningSSE('/api/telemetry-pipelines');
  const sseAdd    = useProvisioningSSE(`/api/telemetry-pipelines/subject-matter-areas/${smaSlug}/capacities`);

  const { data: smaList } = useSubjectMatterAreaList();
  const { data: smaDetail } = useSubjectMatterAreaDetail(smaSlug);
  const assignedCapacityIds = useAssignedCapacityIds();
  const alreadyAssigned = new Set((smaDetail?.capacities ?? []).map((c) => c.capacityId));
  const addPickerExcluded = new Set([...assignedCapacityIds, ...alreadyAssigned]);

  const modeToggle = (
    <div className="flex gap-1 border-b border-[var(--color-border)]">
      {(['create', 'add'] as ConfigMode[]).map((m) => (
        <button
          key={m}
          onClick={() => setMode(m)}
          className={clsx(
            '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
            mode === m
              ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
              : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
          )}
        >
          {m === 'create' ? 'New Subject Matter Area' : 'Add to Existing SMA'}
        </button>
      ))}
    </div>
  );

  return (
    <div className="max-w-2xl space-y-5">
      {modeToggle}

      {/* ── Create SMA ── */}
      {mode === 'create' && (
        <Card className="p-5">
          <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
            Create Subject Matter Area
            <HelpTip
              width="w-80"
              text="An SMA is a logical grouping of Fabric capacities by business domain (e.g. Finance, Operations). Each SMA gets its own Azure Event Hub."
            />
          </h2>
          <p className="mb-5 text-xs text-[var(--color-text-secondary)]">
            Provisions an Azure Event Hub, grants the collection job access, and registers a pipeline entry for each selected capacity.
          </p>
          <div className="space-y-4">
            <div>
              <Input
                isFullWidth
                label="Display Name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={sseCreate.isStreaming}
                placeholder="Finance Operations"
              />
              <SlugPreview displayName={displayName} />
            </div>
            <div>
              <label className="mb-1 flex items-center gap-1 text-sm font-medium text-[var(--color-text-primary)]">
                Capacities
                <HelpTip width="w-72" text="Select Fabric capacities to include. Each capacity can only belong to one SMA. Double-click or drag to move capacities." />
              </label>
              <CapacityPicker value={createIds} onChange={setCreateIds} disabled={sseCreate.isStreaming} excludedIds={assignedCapacityIds} />
            </div>
            {sseCreate.error && (
              <ErrorState title="Provisioning error" message={sseCreate.error} />
            )}
            <Button
              isLoading={sseCreate.isStreaming}
              disabled={sseCreate.isStreaming || !displayName.trim() || createIds.length === 0}
              onClick={() => {
                setShowCreateModal(true);
                sseCreate.start({ displayName, capacityIds: createIds, force: forceReassign });
              }}
            >
              Provision SMA
            </Button>
          </div>
        </Card>
      )}

      {/* ── Add to SMA ── */}
      {mode === 'add' && (
        <Card className="p-5">
          <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
            Add Capacities to Existing SMA
            <HelpTip
              width="w-72"
              text="Expands an already-provisioned SMA. A new Eventstream and pipeline entry will be created for each capacity you add."
            />
          </h2>
          <p className="mb-5 text-xs text-[var(--color-text-secondary)]">
            For adding new capacities to a business domain you've already set up.
          </p>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                Subject Matter Area
              </label>
              <select
                value={smaSlug}
                onChange={(e) => setSmaSlug(e.target.value)}
                disabled={sseAdd.isStreaming}
                className="block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-4 py-2 text-sm text-[var(--color-text-primary)] transition-all duration-200 focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">— select an SMA —</option>
                {(smaList?.smas ?? []).map((s) => (
                  <option key={s.slug} value={s.slug}>{s.displayName}</option>
                ))}
              </select>
            </div>

            {smaSlug && alreadyAssigned.size > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-[var(--color-text-secondary)]">
                  Already assigned
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(smaDetail?.capacities ?? []).map((c) => (
                    <Badge key={c.capacityId} variant="secondary">
                      {c.capacityName}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]">
                Capacities to Add
              </label>
              <CapacityPicker
                value={addIds}
                onChange={setAddIds}
                disabled={sseAdd.isStreaming}
                excludedIds={addPickerExcluded}
              />
            </div>

            {sseAdd.error && (
              <ErrorState title="Provisioning error" message={sseAdd.error} />
            )}

            <Button
              isLoading={sseAdd.isStreaming}
              disabled={sseAdd.isStreaming || !smaSlug}
              onClick={() => {
                setShowAddModal(true);
                sseAdd.start({ capacityIds: addIds });
              }}
            >
              {addIds.length === 0 ? 'Re-provision' : 'Add Capacities'}
            </Button>
          </div>
        </Card>
      )}

      {/* Provisioning modals */}
      {showCreateModal && (
        <ProvisioningModal
          title={`Provisioning: ${displayName || '…'}`}
          logLines={sseCreate.logLines}
          doneEvent={sseCreate.doneEvent}
          isStreaming={sseCreate.isStreaming}
          error={sseCreate.error}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      {showAddModal && (
        <ProvisioningModal
          title={`Adding capacities to: ${smaSlug || '…'}`}
          logLines={sseAdd.logLines}
          doneEvent={sseAdd.doneEvent}
          isStreaming={sseAdd.isStreaming}
          error={sseAdd.error}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bottleneck panel (Spec 217 T030 / US6, FR-005) — "what's wrong right now",
// verdict-first, opens the page regardless of which tab is active.
// ---------------------------------------------------------------------------

interface BottleneckRow {
  capacityId: string;
  name: string;
  currentPct: number | null;
  hoursAbove80Today: number;
  dominantWindow: string | null;
  callout: string;
}

interface TelemetryInsightsPayload {
  bottlenecks: BottleneckRow[];
  health: Array<{ capacityId: string; name: string; lastReadingAt: string | null; freshnessMinutes: number | null; isStale: boolean }>;
  stateEventsToday: Array<{ capacityId: string; name: string; eventType: string; at: string }>;
  alertShortcut: { capacityId: string; name: string; suggestedThresholdPct: number } | null;
}

export function BottleneckPanel() {
  const { data, isLoading } = useQuery<TelemetryInsightsPayload>({
    queryKey: ['telemetry-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: TelemetryInsightsPayload }>('/api/telemetry-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });

  if (isLoading || !data) return null;

  const breaching = data.bottlenecks.filter((b) => b.hoursAbove80Today > 0);
  const staleCount = data.health.filter((h) => h.isStale).length;

  return (
    <Card className="mb-4">
      <h2 className="mb-2 flex items-center gap-2 text-base font-semibold text-[var(--color-text-primary)]">
        {breaching.length > 0 ? (
          <AlertTriangle className="h-4 w-4 text-[var(--color-status-warning)]" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-[var(--color-status-success)]" />
        )}
        {breaching.length > 0 ? `${breaching.length} capacity/ies need attention` : 'Fleet is within normal range'}
      </h2>
      <ul className="space-y-1 text-sm">
        {(breaching.length > 0 ? breaching : data.bottlenecks.slice(0, 3)).map((b) => (
          <li key={b.capacityId} className="text-[var(--color-text-secondary)]">{b.callout}</li>
        ))}
      </ul>
      {staleCount > 0 && (
        <p className="mt-2 text-xs text-[var(--color-status-warning)]">
          {staleCount} capacity/ies have no telemetry reading in the last 30 minutes — eventstream may have stalled.
        </p>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Root — TelemetryDashboard
// ---------------------------------------------------------------------------

export default function TelemetryDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const userRole = user?.role?.toLowerCase() ?? 'viewer';
  const visibleTabs = ALL_TABS.filter((t) => canAccess(userRole, t.minRole));

  const rawTab = searchParams.get('tab') as TabId | null;
  const activeTab: TabId =
    rawTab && visibleTabs.some((t) => t.id === rawTab) ? rawTab : 'live';

  const setActiveTab = (id: TabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    });
  };

  return (
    <PageLayout
      helpId="auto.telemetry"
      title="Capacity Telemetry"
      description="Real-time utilization, pipeline health, and infrastructure management for Fabric capacities."
      icon={<Zap className="h-6 w-6" />}
    >
      {/* Bottleneck panel — verdict-first, always visible (Spec 217 T030) */}
      <BottleneckPanel />

      {/* Tab navigation */}
      <TabNav tabs={visibleTabs} active={activeTab} onChange={setActiveTab} />

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      {/* Tab content */}
      <Extended>
        <div>
          {activeTab === 'live'      && <LiveTab />}
          {activeTab === 'health'    && <HealthTab />}
          {activeTab === 'insights'  && <InsightsTab />}
          {activeTab === 'configure' && <ConfigureTab />}
        </div>
      </Extended>
    </PageLayout>
  );
}
